import { AlertCircle, BarChart3, CheckCircle, ChevronDown, Shield } from 'lucide-react'
import { useState } from 'react'
import { FaFacebook } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  facebookAdsGetCampaignDetails,
  type FacebookAdsGetCampaignDetailsResult,
} from 'sprites-types/workflow/tools/facebookAdsGetCampaignDetails'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function formatCurrency(value: string | null | undefined): string {
  if (!value) return '$0.00'
  const num = parseFloat(value)
  return `$${num.toFixed(2)}`
}

function formatNumber(value: string | null | undefined): string {
  if (!value) return '0'
  return parseFloat(value).toLocaleString()
}

function formatPercent(value: string | null | undefined): string {
  if (!value) return '0%'
  return `${parseFloat(value).toFixed(2)}%`
}

function FacebookAdsGetCampaignDetailsDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(facebookAdsGetCampaignDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)
  const [isExpanded, setIsExpanded] = useState(true)
  const [expandedCampaigns, setExpandedCampaigns] = useState<Record<string, boolean>>({})
  const [expandedInsights, setExpandedInsights] = useState<Record<string, boolean>>({})
  const [expandedMetaVerification, setExpandedMetaVerification] = useState<Record<string, boolean>>(
    {}
  )

  const toggleCampaign = (campaignId: string) => {
    setExpandedCampaigns((prev) => ({ ...prev, [campaignId]: !prev[campaignId] }))
  }

  const toggleInsightDetails = (key: string) => {
    setExpandedInsights((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const toggleMetaVerification = (key: string) => {
    setExpandedMetaVerification((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const results: FacebookAdsGetCampaignDetailsResult[] = Array.isArray(parseResult.data)
    ? parseResult.data
    : [parseResult.data]

  const result = results[0]
  const { campaigns, summary } = result

  const campaignsWithInsights = campaigns.filter(
    (campaign) => campaign.insights && campaign.insights.length > 0
  ).length

  if (campaigns.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No campaign performance data found</div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
          <div className="px-4 py-3">
            <div
              className={`flex items-center justify-between gap-2 ${campaigns.length > 1 ? 'mb-3' : ''}`}
            >
              <div className="flex items-center gap-2">
                <FaFacebook className="h-4 w-4 text-[#0064e0]" />
                <div className="text-sm font-semibold text-gray-900">Campaign Details</div>
              </div>
              {summary.date_range && (
                <div className="flex items-center gap-2">
                  <div className="text-xs text-gray-400">Data may have ~24h delay</div>
                  <div className="flex items-center gap-1.5 rounded-lg border-2 border-[#0064e0] bg-blue-50 px-3 py-1.5 shadow-sm">
                    <span className="text-sm font-bold text-[#0064e0]">
                      {new Date(summary.date_range.start).toLocaleDateString()} -{' '}
                      {new Date(summary.date_range.end).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {campaigns.length > 1 && (
              <div
                className={`rounded-2xl border border-gray-200 bg-gray-50 p-3 ${campaigns.length > 1 ? 'mb-3' : ''}`}
              >
                <div className="mb-2 text-xs font-medium text-gray-500">Summary</div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {/* PRIORITY ORDER: Revenue, Spend, ROAS, Meta ROAS, Conversions, then rest */}

                  {/* PRIORITY 1: Revenue */}
                  {summary.total_revenue !== null && summary.total_revenue !== undefined && (
                    <div>
                      <div className="text-gray-500">Total Revenue</div>
                      <div className="font-semibold text-green-600">
                        ${summary.total_revenue.toFixed(2)}
                      </div>
                    </div>
                  )}

                  {/* PRIORITY 2: Spend */}
                  {summary.total_spend !== null && summary.total_spend !== undefined && (
                    <div>
                      <div className="text-gray-500">Total Spend</div>
                      <div className="font-semibold text-gray-900">
                        ${summary.total_spend.toFixed(2)}
                      </div>
                    </div>
                  )}

                  {/* PRIORITY 3: ROAS */}
                  {summary.roas !== null && summary.roas !== undefined && summary.roas > 0 && (
                    <div>
                      <div className="text-gray-500">ROAS (Calculated)</div>
                      <div className="font-semibold text-blue-600">{summary.roas.toFixed(2)}x</div>
                    </div>
                  )}

                  {/* PRIORITY 4: Meta ROAS */}
                  {summary.meta_roas !== null &&
                    summary.meta_roas !== undefined &&
                    summary.meta_roas > 0 && (
                      <div>
                        <div className="text-gray-500">ROAS (Meta)</div>
                        <div className="font-semibold text-purple-600">
                          {summary.meta_roas.toFixed(2)}x
                        </div>
                      </div>
                    )}

                  {/* PRIORITY 5: Conversions */}
                  {summary.total_conversions !== null &&
                    summary.total_conversions !== undefined && (
                      <div>
                        <div className="text-gray-500">Conversions</div>
                        <div className="font-semibold text-gray-900">
                          {summary.total_conversions.toLocaleString()}
                        </div>
                      </div>
                    )}

                  {/* Campaign Count */}
                  <div>
                    <div className="text-gray-500">Campaigns Returned</div>
                    <div className="font-semibold text-gray-900">
                      {summary.total_campaigns}
                      {campaignsWithInsights < summary.total_campaigns && (
                        <span className="ml-1 text-xs font-normal text-gray-500">
                          ({campaignsWithInsights} with insights)
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Other metrics */}
                  {summary.total_impressions !== null &&
                    summary.total_impressions !== undefined && (
                      <div>
                        <div className="text-gray-500">Total Impressions</div>
                        <div className="font-semibold text-gray-900">
                          {summary.total_impressions.toLocaleString()}
                        </div>
                      </div>
                    )}

                  {summary.total_clicks !== null && summary.total_clicks !== undefined && (
                    <div>
                      <div className="text-gray-500">Total Clicks</div>
                      <div className="font-semibold text-gray-900">
                        {summary.total_clicks.toLocaleString()}
                      </div>
                    </div>
                  )}

                  {summary.average_ctr !== null && summary.average_ctr !== undefined && (
                    <div>
                      <div className="text-gray-500">Avg CTR</div>
                      <div className="font-semibold text-gray-900">
                        {summary.average_ctr.toFixed(2)}%
                      </div>
                    </div>
                  )}

                  {summary.average_cpc !== null && summary.average_cpc !== undefined && (
                    <div>
                      <div className="text-gray-500">Avg CPC</div>
                      <div className="font-semibold text-gray-900">
                        ${summary.average_cpc.toFixed(2)}
                      </div>
                    </div>
                  )}

                  {summary.average_cost_per_conversion !== null &&
                    summary.average_cost_per_conversion !== undefined && (
                      <div>
                        <div className="text-gray-500">Avg Cost/Conv</div>
                        <div className="font-semibold text-orange-600">
                          ${summary.average_cost_per_conversion.toFixed(2)}
                        </div>
                      </div>
                    )}

                  {summary.average_cpm !== null && summary.average_cpm !== undefined && (
                    <div>
                      <div className="text-gray-500">Avg CPM</div>
                      <div className="font-semibold text-gray-900">
                        ${summary.average_cpm.toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
                {summary.top_performing_campaign && (
                  <div className="mt-3 border-t border-gray-200 pt-2">
                    <div className="text-xs text-gray-500">Top Performing</div>
                    <div className="text-xs font-medium text-gray-900">
                      {summary.top_performing_campaign.name}
                    </div>
                  </div>
                )}
              </div>
            )}

            {campaigns.length > 1 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex w-full items-center justify-between text-xs text-gray-600 hover:text-gray-900"
              >
                <span>{isExpanded ? 'Hide' : 'Show'} campaign details</span>
                <ChevronDown
                  className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>
            )}
          </div>
        </div>
      </ContextMenuWrapper>

      {isExpanded && campaigns.length > 0 && (
        <div className="space-y-2">
          {campaigns.map((campaign) => {
            const isCampaignExpanded = expandedCampaigns[campaign.campaign_id] || false
            const hasInsights = campaign.insights && campaign.insights.length > 0

            return (
              <div
                key={campaign.campaign_id}
                className="mx-8 rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm backdrop-blur-[8px]"
              >
                {hasInsights ? (
                  <button
                    onClick={() => toggleCampaign(campaign.campaign_id)}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
                  >
                    <ChevronDown
                      className={`mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${isCampaignExpanded ? 'rotate-180' : ''}`}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {campaign.campaign_name}
                      </div>
                      <div className="text-xs text-gray-500">ID: {campaign.campaign_id}</div>
                      {campaign.objective && (
                        <div className="mt-1 text-xs text-gray-600">
                          Objective: {campaign.objective.replace('OUTCOME_', '')}
                        </div>
                      )}
                      {campaign.status && (
                        <span
                          className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            campaign.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : campaign.status === 'PAUSED'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {campaign.status}
                        </span>
                      )}
                    </div>
                  </button>
                ) : (
                  <div className="flex w-full items-start gap-3 px-4 py-3">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {campaign.campaign_name}
                      </div>
                      <div className="text-xs text-gray-500">ID: {campaign.campaign_id}</div>
                      {campaign.objective && (
                        <div className="mt-1 text-xs text-gray-600">
                          Objective: {campaign.objective.replace('OUTCOME_', '')}
                        </div>
                      )}
                      {campaign.status && (
                        <span
                          className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            campaign.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : campaign.status === 'PAUSED'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {campaign.status}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {isCampaignExpanded && !hasInsights && (
                  <div className="px-4 pb-3">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-center text-xs text-gray-500">
                      No insights found for this date range
                    </div>
                  </div>
                )}

                {isCampaignExpanded && hasInsights && (
                  <div className="space-y-2 px-4 pb-3">
                    {campaign.insights?.map((insight, idx) => {
                      const insightKey = `${campaign.campaign_id}-${idx}`
                      const isInsightExpanded = expandedInsights[insightKey] || false
                      const hasAdditionalMetrics =
                        insight.reach ||
                        insight.frequency ||
                        insight.cpc ||
                        insight.cpm ||
                        insight.cpp ||
                        insight.conversions ||
                        insight.cost_per_conversion ||
                        insight.age ||
                        insight.gender ||
                        insight.country ||
                        insight.region ||
                        insight.device_platform ||
                        insight.publisher_platform ||
                        insight.platform_position

                      return (
                        <div key={idx} className="rounded-xl border border-gray-200 bg-gray-50 p-2">
                          {insight.date_start && insight.date_stop && (
                            <div className="mb-1 text-xs text-gray-500">
                              {new Date(insight.date_start).toLocaleDateString()} -{' '}
                              {new Date(insight.date_stop).toLocaleDateString()}
                            </div>
                          )}

                          {/* Basic Metrics - Ordered by priority: Revenue, Spend, ROAS, Meta ROAS, Conversions, then rest */}
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            {/* PRIORITY METRICS FIRST */}

                            {/* Calculate and show revenue if conversion actions with values exist */}
                            {insight.conversions &&
                              typeof insight.conversions !== 'string' &&
                              Array.isArray(insight.conversions) &&
                              insight.conversions.some(
                                (action) => action.action_type === 'omni_purchase' && action.value
                              ) && (
                                <div className="rounded-lg border border-green-200 bg-green-50 p-2">
                                  <div className="text-green-700">Revenue</div>
                                  <div className="mt-0.5 text-sm font-semibold text-green-800">
                                    {formatCurrency(
                                      insight.conversions
                                        .filter((action) => action.action_type === 'omni_purchase')
                                        .reduce(
                                          (sum, action) => sum + parseFloat(action.value || '0'),
                                          0
                                        )
                                        .toString()
                                    )}
                                  </div>
                                </div>
                              )}

                            {insight.spend && (
                              <div className="rounded-lg bg-white p-2">
                                <div className="text-gray-500">Spend</div>
                                <div className="mt-0.5 text-sm font-semibold text-gray-900">
                                  {formatCurrency(insight.spend)}
                                </div>
                              </div>
                            )}

                            {/* Calculate ROAS if we have both revenue and spend */}
                            {insight.conversions &&
                              typeof insight.conversions !== 'string' &&
                              Array.isArray(insight.conversions) &&
                              insight.conversions.some(
                                (action) => action.action_type === 'omni_purchase' && action.value
                              ) &&
                              insight.spend &&
                              parseFloat(insight.spend) > 0 && (
                                <div className="rounded-lg border border-blue-200 bg-blue-50 p-2">
                                  <div className="text-blue-700">ROAS</div>
                                  <div className="mt-0.5 text-sm font-semibold text-blue-800">
                                    {(
                                      insight.conversions
                                        .filter((action) => action.action_type === 'omni_purchase')
                                        .reduce(
                                          (sum, action) => sum + parseFloat(action.value || '0'),
                                          0
                                        ) / parseFloat(insight.spend)
                                    ).toFixed(2)}
                                    x
                                  </div>
                                </div>
                              )}

                            {/* Show total conversions if available */}
                            {insight.conversions && (
                              <div className="rounded-lg bg-white p-2">
                                <div className="text-gray-500">Conversions</div>
                                <div className="mt-0.5 text-sm font-semibold text-gray-900">
                                  {typeof insight.conversions === 'string'
                                    ? formatNumber(insight.conversions)
                                    : formatNumber(
                                        insight.conversions
                                          .reduce(
                                            (sum, action) => sum + parseFloat(action.value || '0'),
                                            0
                                          )
                                          .toString()
                                      )}
                                </div>
                              </div>
                            )}

                            {/* STANDARD METRICS */}
                            {insight.impressions && (
                              <div className="rounded-lg bg-white p-2">
                                <div className="text-gray-500">Impressions</div>
                                <div className="mt-0.5 text-sm font-semibold text-gray-900">
                                  {formatNumber(insight.impressions)}
                                </div>
                              </div>
                            )}

                            {insight.clicks && (
                              <div className="rounded-lg bg-white p-2">
                                <div className="text-gray-500">Clicks</div>
                                <div className="mt-0.5 text-sm font-semibold text-gray-900">
                                  {formatNumber(insight.clicks)}
                                </div>
                              </div>
                            )}

                            {insight.ctr && (
                              <div className="rounded-lg bg-white p-2">
                                <div className="text-gray-500">CTR</div>
                                <div className="mt-0.5 text-sm font-semibold text-gray-900">
                                  {formatPercent(insight.ctr)}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Show Details Button - Only if there are additional metrics */}
                          {hasAdditionalMetrics && (
                            <button
                              onClick={() => toggleInsightDetails(insightKey)}
                              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
                            >
                              <span>{isInsightExpanded ? 'Hide' : 'Show'} all metrics</span>
                              <ChevronDown
                                className={`h-3.5 w-3.5 transition-transform ${isInsightExpanded ? 'rotate-180' : ''}`}
                              />
                            </button>
                          )}

                          {/* All Metrics - Conditionally Visible */}
                          {isInsightExpanded && hasAdditionalMetrics && (
                            <div className="mt-3 space-y-3">
                              {/* Additional Metrics */}
                              <div className="rounded-lg border border-gray-200 bg-white p-3">
                                <div className="mb-2 text-xs font-semibold text-gray-700">
                                  Additional Metrics
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-xs">
                                  {insight.reach && (
                                    <div className="rounded-md bg-gray-50 p-2">
                                      <div className="text-gray-500">Reach</div>
                                      <div className="mt-0.5 font-semibold text-gray-900">
                                        {formatNumber(insight.reach)}
                                      </div>
                                    </div>
                                  )}
                                  {insight.frequency && (
                                    <div className="rounded-md bg-gray-50 p-2">
                                      <div className="text-gray-500">Frequency</div>
                                      <div className="mt-0.5 font-semibold text-gray-900">
                                        {parseFloat(insight.frequency).toFixed(2)}
                                      </div>
                                    </div>
                                  )}
                                  {insight.cpc && (
                                    <div className="rounded-md bg-gray-50 p-2">
                                      <div className="text-gray-500">CPC</div>
                                      <div className="mt-0.5 font-semibold text-gray-900">
                                        {formatCurrency(insight.cpc)}
                                      </div>
                                    </div>
                                  )}
                                  {insight.cpm && (
                                    <div className="rounded-md bg-gray-50 p-2">
                                      <div className="text-gray-500">CPM</div>
                                      <div className="mt-0.5 font-semibold text-gray-900">
                                        {formatCurrency(insight.cpm)}
                                      </div>
                                    </div>
                                  )}
                                  {insight.cpp && (
                                    <div className="rounded-md bg-gray-50 p-2">
                                      <div className="text-gray-500">CPP</div>
                                      <div className="mt-0.5 font-semibold text-gray-900">
                                        {formatCurrency(insight.cpp)}
                                      </div>
                                    </div>
                                  )}
                                  {insight.conversions && (
                                    <div className="rounded-md bg-gray-50 p-2">
                                      <div className="text-gray-500">Total Conversions</div>
                                      <div className="mt-0.5 font-semibold text-gray-900">
                                        {typeof insight.conversions === 'string'
                                          ? formatNumber(insight.conversions)
                                          : formatNumber(
                                              insight.conversions
                                                .reduce(
                                                  (sum, action) =>
                                                    sum + parseFloat(action.value || '0'),
                                                  0
                                                )
                                                .toString()
                                            )}
                                      </div>
                                    </div>
                                  )}
                                  {insight.cost_per_conversion &&
                                    Array.isArray(insight.cost_per_conversion) &&
                                    insight.cost_per_conversion.find(
                                      (c) => c.action_type === 'omni_purchase'
                                    ) && (
                                      <div className="rounded-md bg-gray-50 p-2">
                                        <div className="text-gray-500">Cost/Conv</div>
                                        <div className="mt-0.5 font-semibold text-orange-600">
                                          {formatCurrency(
                                            insight.cost_per_conversion.find(
                                              (c) => c.action_type === 'omni_purchase'
                                            )?.value || '0'
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>

                              {/* Conversions Breakdown */}
                              {insight.conversions &&
                                typeof insight.conversions !== 'string' &&
                                insight.conversions.length > 0 && (
                                  <div className="rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 p-3">
                                    <div className="mb-2 text-xs font-semibold text-emerald-900">
                                      Conversion Breakdown
                                    </div>
                                    <div className="space-y-1.5 text-xs">
                                      {insight.conversions.map((action, actionIdx) => (
                                        <div
                                          key={actionIdx}
                                          className="flex items-center justify-between rounded-md bg-white/60 px-2 py-1.5"
                                        >
                                          <span className="font-medium capitalize text-emerald-700">
                                            {action.action_type?.replace(/_/g, ' ')}
                                          </span>
                                          <span className="font-semibold text-emerald-900">
                                            {action.value}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                              {/* Cost Per Conversion Breakdown */}
                              {insight.cost_per_conversion &&
                                typeof insight.cost_per_conversion !== 'string' &&
                                insight.cost_per_conversion.length > 0 && (
                                  <div className="rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 p-3">
                                    <div className="mb-2 text-xs font-semibold text-violet-900">
                                      Cost Per Conversion
                                    </div>
                                    <div className="space-y-1.5 text-xs">
                                      {insight.cost_per_conversion.map((action, actionIdx) => (
                                        <div
                                          key={actionIdx}
                                          className="flex items-center justify-between rounded-md bg-white/60 px-2 py-1.5"
                                        >
                                          <span className="font-medium capitalize text-violet-700">
                                            {action.action_type?.replace(/_/g, ' ')}
                                          </span>
                                          <span className="font-semibold text-violet-900">
                                            {formatCurrency(action.value || '0')}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                              {/* Breakdown Demographics */}
                              {(insight.age ||
                                insight.gender ||
                                insight.country ||
                                insight.device_platform) && (
                                <div className="rounded-lg border border-sky-200 bg-gradient-to-br from-sky-50 to-blue-50 p-3">
                                  <div className="mb-2 text-xs font-semibold text-sky-900">
                                    Audience Demographics
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    {insight.age && (
                                      <div className="rounded-md bg-white/60 px-2 py-1.5">
                                        <span className="text-sky-600">Age:</span>{' '}
                                        <span className="font-semibold text-sky-900">
                                          {insight.age}
                                        </span>
                                      </div>
                                    )}
                                    {insight.gender && (
                                      <div className="rounded-md bg-white/60 px-2 py-1.5">
                                        <span className="text-sky-600">Gender:</span>{' '}
                                        <span className="font-semibold text-sky-900">
                                          {insight.gender}
                                        </span>
                                      </div>
                                    )}
                                    {insight.country && (
                                      <div className="rounded-md bg-white/60 px-2 py-1.5">
                                        <span className="text-sky-600">Country:</span>{' '}
                                        <span className="font-semibold text-sky-900">
                                          {insight.country}
                                        </span>
                                      </div>
                                    )}
                                    {insight.region && (
                                      <div className="rounded-md bg-white/60 px-2 py-1.5">
                                        <span className="text-sky-600">Region:</span>{' '}
                                        <span className="font-semibold text-sky-900">
                                          {insight.region}
                                        </span>
                                      </div>
                                    )}
                                    {insight.device_platform && (
                                      <div className="rounded-md bg-white/60 px-2 py-1.5">
                                        <span className="text-sky-600">Device:</span>{' '}
                                        <span className="font-semibold text-sky-900">
                                          {insight.device_platform}
                                        </span>
                                      </div>
                                    )}
                                    {insight.publisher_platform && (
                                      <div className="rounded-md bg-white/60 px-2 py-1.5">
                                        <span className="text-sky-600">Platform:</span>{' '}
                                        <span className="font-semibold text-sky-900">
                                          {insight.publisher_platform}
                                        </span>
                                      </div>
                                    )}
                                    {insight.platform_position && (
                                      <div className="rounded-md bg-white/60 px-2 py-1.5">
                                        <span className="text-sky-600">Position:</span>{' '}
                                        <span className="font-semibold text-sky-900">
                                          {insight.platform_position}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Meta Verification Section */}
                              {(insight.meta_action_values ||
                                insight.meta_purchase_roas ||
                                insight.meta_reported_revenue !== undefined) && (
                                <div className="rounded-lg border border-purple-200 bg-gradient-to-br from-purple-50 to-violet-50 p-3">
                                  <button
                                    onClick={() => toggleMetaVerification(insightKey)}
                                    className="mb-2 flex w-full items-center justify-between text-left"
                                  >
                                    <div className="flex items-center gap-2">
                                      <Shield className="h-4 w-4 text-purple-600" />
                                      <span className="text-xs font-semibold text-purple-900">
                                        Meta Verification
                                      </span>
                                      <span className="rounded-full bg-purple-200 px-2 py-0.5 text-xs font-medium text-purple-800">
                                        Raw Data
                                      </span>
                                    </div>
                                    <ChevronDown
                                      className={`h-3.5 w-3.5 text-purple-400 transition-transform ${expandedMetaVerification[insightKey] ? 'rotate-180' : ''}`}
                                    />
                                  </button>

                                  {expandedMetaVerification[insightKey] && (
                                    <div className="space-y-3">
                                      {/* Comparison Table */}
                                      <div className="overflow-hidden rounded-lg border border-purple-300 bg-white">
                                        <div className="bg-purple-100 px-3 py-2">
                                          <h5 className="text-xs font-semibold text-purple-900">
                                            Calculated vs Meta Reported
                                          </h5>
                                        </div>
                                        <div className="p-3">
                                          <div className="space-y-2 text-xs">
                                            {/* Revenue Comparison */}
                                            {insight.meta_reported_revenue !== undefined && (
                                              <div className="flex items-center justify-between">
                                                <span className="font-medium text-gray-700">
                                                  Revenue:
                                                </span>
                                                <div className="flex items-center gap-3 text-right">
                                                  <span className="text-gray-600">
                                                    {insight.conversions &&
                                                    typeof insight.conversions !== 'string' &&
                                                    Array.isArray(insight.conversions) &&
                                                    insight.conversions.some(
                                                      (action) =>
                                                        action.action_type === 'omni_purchase' &&
                                                        action.value
                                                    )
                                                      ? formatCurrency(
                                                          insight.conversions
                                                            .filter(
                                                              (action) =>
                                                                action.action_type ===
                                                                'omni_purchase'
                                                            )
                                                            .reduce(
                                                              (sum, action) =>
                                                                sum +
                                                                parseFloat(action.value || '0'),
                                                              0
                                                            )
                                                            .toString()
                                                        )
                                                      : '$0.00'}
                                                  </span>
                                                  <span className="font-medium text-purple-600">
                                                    {formatCurrency(
                                                      (
                                                        insight.meta_reported_revenue ?? 0
                                                      ).toString()
                                                    )}
                                                  </span>
                                                  {(() => {
                                                    const calculatedRevenue =
                                                      insight.conversions &&
                                                      typeof insight.conversions !== 'string' &&
                                                      Array.isArray(insight.conversions) &&
                                                      insight.conversions.some(
                                                        (action) =>
                                                          action.action_type === 'omni_purchase' &&
                                                          action.value
                                                      )
                                                        ? insight.conversions
                                                            .filter(
                                                              (action) =>
                                                                action.action_type ===
                                                                'omni_purchase'
                                                            )
                                                            .reduce(
                                                              (sum, action) =>
                                                                sum +
                                                                parseFloat(action.value || '0'),
                                                              0
                                                            )
                                                        : 0
                                                    const metaRevenue =
                                                      insight.meta_reported_revenue || 0
                                                    const diff = Math.abs(
                                                      calculatedRevenue - metaRevenue
                                                    )
                                                    const maxValue = Math.max(
                                                      calculatedRevenue,
                                                      metaRevenue,
                                                      1
                                                    )

                                                    if (diff < 0.01) {
                                                      return (
                                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                                      )
                                                    } else if (diff / maxValue < 0.05) {
                                                      return (
                                                        <AlertCircle className="h-4 w-4 text-yellow-500" />
                                                      )
                                                    } else {
                                                      return (
                                                        <AlertCircle className="h-4 w-4 text-red-500" />
                                                      )
                                                    }
                                                  })()}
                                                </div>
                                              </div>
                                            )}

                                            {/* ROAS Comparison */}
                                            {insight.meta_reported_roas !== undefined && (
                                              <div className="flex items-center justify-between">
                                                <span className="font-medium text-gray-700">
                                                  ROAS:
                                                </span>
                                                <div className="flex items-center gap-3 text-right">
                                                  <span className="text-gray-600">
                                                    {(() => {
                                                      const calculatedRevenue =
                                                        insight.conversions &&
                                                        typeof insight.conversions !== 'string' &&
                                                        Array.isArray(insight.conversions) &&
                                                        insight.conversions.some(
                                                          (action) =>
                                                            action.action_type ===
                                                              'omni_purchase' && action.value
                                                        )
                                                          ? insight.conversions
                                                              .filter(
                                                                (action) =>
                                                                  action.action_type ===
                                                                  'omni_purchase'
                                                              )
                                                              .reduce(
                                                                (sum, action) =>
                                                                  sum +
                                                                  parseFloat(action.value || '0'),
                                                                0
                                                              )
                                                          : 0
                                                      const spend = parseFloat(insight.spend || '0')
                                                      return spend > 0
                                                        ? (calculatedRevenue / spend).toFixed(2) +
                                                            'x'
                                                        : '0.00x'
                                                    })()}
                                                  </span>
                                                  <span className="font-medium text-purple-600">
                                                    {(insight.meta_reported_roas ?? 0).toFixed(2)}x
                                                  </span>
                                                  {(() => {
                                                    const calculatedRevenue =
                                                      insight.conversions &&
                                                      typeof insight.conversions !== 'string' &&
                                                      Array.isArray(insight.conversions) &&
                                                      insight.conversions.some(
                                                        (action) =>
                                                          action.action_type === 'omni_purchase' &&
                                                          action.value
                                                      )
                                                        ? insight.conversions
                                                            .filter(
                                                              (action) =>
                                                                action.action_type ===
                                                                'omni_purchase'
                                                            )
                                                            .reduce(
                                                              (sum, action) =>
                                                                sum +
                                                                parseFloat(action.value || '0'),
                                                              0
                                                            )
                                                        : 0
                                                    const spend = parseFloat(insight.spend || '0')
                                                    const calculatedRoas =
                                                      spend > 0 ? calculatedRevenue / spend : 0
                                                    const metaRoas = insight.meta_reported_roas || 0
                                                    const diff = Math.abs(calculatedRoas - metaRoas)
                                                    const maxValue = Math.max(
                                                      calculatedRoas,
                                                      metaRoas,
                                                      1
                                                    )

                                                    if (diff < 0.01) {
                                                      return (
                                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                                      )
                                                    } else if (diff / maxValue < 0.05) {
                                                      return (
                                                        <AlertCircle className="h-4 w-4 text-yellow-500" />
                                                      )
                                                    } else {
                                                      return (
                                                        <AlertCircle className="h-4 w-4 text-red-500" />
                                                      )
                                                    }
                                                  })()}
                                                </div>
                                              </div>
                                            )}
                                          </div>

                                          {/* Legend */}
                                          <div className="mt-3 flex items-center gap-4 border-t border-gray-200 pt-2 text-xs text-gray-500">
                                            <span className="text-gray-600">Calculated</span>
                                            <span className="text-purple-600">Meta Reported</span>
                                            <div className="flex items-center gap-2">
                                              <CheckCircle className="h-3 w-3 text-green-500" />
                                              <span>Match</span>
                                              <AlertCircle className="h-3 w-3 text-yellow-500" />
                                              <span>~5%</span>
                                              <AlertCircle className="h-3 w-3 text-red-500" />
                                              <span>Divergent</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Raw Meta Data */}
                                      {insight.meta_action_values &&
                                        Array.isArray(insight.meta_action_values) &&
                                        insight.meta_action_values.length > 0 && (
                                          <div className="rounded-lg border border-gray-300 bg-gray-100 p-3">
                                            <h6 className="mb-2 text-xs font-semibold text-gray-900">
                                              Raw Action Values
                                            </h6>
                                            <div className="space-y-1 text-xs">
                                              {insight.meta_action_values
                                                .filter(
                                                  (av: any) => parseFloat(av.value || '0') > 0
                                                )
                                                .map((action: any, index: number) => (
                                                  <div key={index} className="flex justify-between">
                                                    <span className="text-gray-600">
                                                      {action.action_type}
                                                    </span>
                                                    <span className="font-mono text-gray-900">
                                                      {action.action_type === 'omni_purchase'
                                                        ? formatCurrency(action.value)
                                                        : action.value}
                                                    </span>
                                                  </div>
                                                ))}
                                            </div>
                                          </div>
                                        )}

                                      {insight.meta_purchase_roas &&
                                        Array.isArray(insight.meta_purchase_roas) &&
                                        insight.meta_purchase_roas.length > 0 && (
                                          <div className="rounded-lg border border-gray-300 bg-gray-100 p-3">
                                            <h6 className="mb-2 text-xs font-semibold text-gray-900">
                                              Raw Purchase ROAS
                                            </h6>
                                            <div className="space-y-1 text-xs">
                                              {insight.meta_purchase_roas.map(
                                                (roas: any, index: number) => (
                                                  <div key={index} className="flex justify-between">
                                                    <span className="text-gray-600">
                                                      {roas.action_type}
                                                    </span>
                                                    <span className="font-mono text-gray-900">
                                                      {parseFloat(roas.value).toFixed(2)}x
                                                    </span>
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function downloadFacebookAdsGetCampaignDetails(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function facebookAdsGetCampaignDetailsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsGetCampaignDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const results: FacebookAdsGetCampaignDetailsResult[] = Array.isArray(parseResult.data)
    ? parseResult.data
    : [parseResult.data]

  const result = results[0]
  const { campaigns, summary } = result

  if (campaigns.length === 0) {
    return `## Facebook Campaign Details

No campaign data found.

---
`
  }

  const summarySection = `### Summary

- **Total Campaigns:** ${summary.total_campaigns}
${summary.total_spend !== null && summary.total_spend !== undefined ? `- **Total Spend:** $${summary.total_spend.toFixed(2)}` : ''}
${summary.total_impressions !== null && summary.total_impressions !== undefined ? `- **Total Impressions:** ${summary.total_impressions.toLocaleString()}` : ''}
${summary.total_clicks !== null && summary.total_clicks !== undefined ? `- **Total Clicks:** ${summary.total_clicks.toLocaleString()}` : ''}
${summary.total_revenue !== null && summary.total_revenue !== undefined ? `- **Total Revenue:** $${summary.total_revenue.toFixed(2)}` : ''}
${summary.total_conversions !== null && summary.total_conversions !== undefined ? `- **Total Conversions:** ${summary.total_conversions.toLocaleString()}` : ''}
${summary.roas !== null && summary.roas !== undefined && summary.roas > 0 ? `- **ROAS (Calculated):** ${summary.roas.toFixed(2)}x` : ''}
${summary.meta_roas !== null && summary.meta_roas !== undefined && summary.meta_roas > 0 ? `- **ROAS (Meta):** ${summary.meta_roas.toFixed(2)}x` : ''}
${summary.average_ctr !== null && summary.average_ctr !== undefined ? `- **Average CTR:** ${summary.average_ctr.toFixed(2)}%` : ''}
${summary.average_cpc !== null && summary.average_cpc !== undefined ? `- **Average CPC:** $${summary.average_cpc.toFixed(2)}` : ''}
${summary.average_cpm !== null && summary.average_cpm !== undefined ? `- **Average CPM:** $${summary.average_cpm.toFixed(2)}` : ''}
`

  const campaignsMarkdown = campaigns
    .map((campaign) => {
      const insightsText =
        campaign.insights && campaign.insights.length > 0
          ? campaign.insights
              .map(
                (insight) => `
**Insights:**
${insight.impressions ? `- Impressions: ${formatNumber(insight.impressions)}` : ''}
${insight.clicks ? `- Clicks: ${formatNumber(insight.clicks)}` : ''}
${insight.spend ? `- Spend: ${formatCurrency(insight.spend)}` : ''}
${insight.reach ? `- Reach: ${formatNumber(insight.reach)}` : ''}
${insight.frequency ? `- Frequency: ${parseFloat(insight.frequency).toFixed(2)}` : ''}
${insight.ctr ? `- CTR: ${formatPercent(insight.ctr)}` : ''}
${insight.cpc ? `- CPC: ${formatCurrency(insight.cpc)}` : ''}
${insight.cpm ? `- CPM: ${formatCurrency(insight.cpm)}` : ''}
${insight.cpp ? `- CPP: ${formatCurrency(insight.cpp)}` : ''}
${insight.conversions ? `- Conversions: ${typeof insight.conversions === 'string' ? formatNumber(insight.conversions) : formatNumber(insight.conversions.reduce((sum, action) => sum + parseFloat(action.value || '0'), 0).toString())}` : ''}
${insight.cost_per_conversion && Array.isArray(insight.cost_per_conversion) && insight.cost_per_conversion.find((c) => c.action_type === 'omni_purchase') ? `- Cost/Conversion: ${formatCurrency(insight.cost_per_conversion.find((c) => c.action_type === 'omni_purchase')?.value || '0')}` : ''}

${
  insight.age || insight.gender || insight.country || insight.device_platform
    ? `**Demographics:**
${insight.age ? `- Age: ${insight.age}` : ''}
${insight.gender ? `- Gender: ${insight.gender}` : ''}
${insight.country ? `- Country: ${insight.country}` : ''}
${insight.region ? `- Region: ${insight.region}` : ''}
${insight.device_platform ? `- Device: ${insight.device_platform}` : ''}
${insight.publisher_platform ? `- Platform: ${insight.publisher_platform}` : ''}
${insight.platform_position ? `- Position: ${insight.platform_position}` : ''}
`
    : ''
}
`
              )
              .join('\n')
          : ''

      return `### ${campaign.campaign_name}

**ID:** ${campaign.campaign_id}
${campaign.objective ? `**Objective:** ${campaign.objective.replace('OUTCOME_', '')}` : ''}
${campaign.status ? `**Status:** ${campaign.status}` : ''}

${insightsText}
`
    })
    .join('\n---\n\n')

  return `## Facebook Campaign Details

${summarySection}

---

${campaignsMarkdown}

---
`
}

export const FacebookAdsGetCampaignDetailsDisplay = createWorkflowToolComponent(
  FacebookAdsGetCampaignDetailsDisplayComponent,
  downloadFacebookAdsGetCampaignDetails,
  facebookAdsGetCampaignDetailsToMarkdown
)
